import { legalAcceptanceRequired, presentLegalAcceptance } from "../legal_acceptance.js"
import { Controller } from "@hotwired/stimulus"

const IDLE_AFTER_MS = 5 * 60 * 1000
const FLUSH_EVERY_MS = 60 * 1000
const TICK_EVERY_MS = 1000

export default class extends Controller {
  static values = { url: String, deferred: Boolean }

  connect() {
    this.enabled = false
    this.telemetryRejected ||= false
    if (!this.deferredValue || this.element.dataset.pdfReaderAccessMode === "online") this.enable()
  }

  enable() {
    if (this.enabled) return

    this.enabled = true
    this.startSession(performance.now())
    this.tickTimer = window.setInterval(() => this.tick(), TICK_EVERY_MS)
    this.flushTimer = window.setInterval(() => this.flush(), FLUSH_EVERY_MS)
  }

  disconnect() {
    if (!this.enabled) return

    this.tick()
    this.flush(false)
    window.clearInterval(this.tickTimer)
    window.clearInterval(this.flushTimer)
  }

  engage() {
    if (!this.enabled) return

    const now = performance.now()
    this.tick(now)
    if (this.paused) this.startSession(now)
    this.lastActivityAt = now
    this.lastTickAt = now
  }

  focus() {
    if (!this.enabled) return

    // Do not include time elapsed while the window was unfocused.
    this.lastTickAt = performance.now()
    this.engage()
  }

  blur() {
    if (!this.enabled) return

    this.tick()
    this.flush(false)
  }

  visibilityChanged() {
    if (!this.enabled) return

    if (document.visibilityState === "visible") {
      this.lastTickAt = performance.now()
      this.engage()
    } else {
      this.tick()
      this.flush(false)
    }
  }

  pageHide() {
    if (!this.enabled) return

    this.tick()
    this.flush(false)
  }

  tick(now = performance.now()) {
    const startedAt = this.lastTickAt
    this.lastTickAt = now
    if (this.paused || !this.trackingAllowed()) return

    const idleAt = this.lastActivityAt + IDLE_AFTER_MS
    const countedUntil = Math.min(now, idleAt)
    this.activeMilliseconds += Math.max(0, countedUntil - startedAt)

    if (now >= idleAt) {
      this.paused = true
      this.flush(false)
    }
  }

  trackingAllowed() {
    return document.visibilityState === "visible" && document.hasFocus()
  }

  startSession(now) {
    this.sessionId = crypto.randomUUID()
    this.activeMilliseconds = 0
    this.lastSentSeconds = -1
    this.lastActivityAt = now
    this.lastTickAt = now
    this.paused = false
    this.flush(false)
  }

  async flush(updateClock = true) {
    if (!this.enabled || this.telemetryRejected) return

    if (updateClock) this.tick()

    const activeSeconds = Math.floor(this.activeMilliseconds / 1000)
    if (activeSeconds === this.lastSentSeconds) return

    const sessionId = this.sessionId
    this.lastSentSeconds = activeSeconds

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: this.headers(),
        body: JSON.stringify({ reading_time: { session_id: sessionId, active_seconds: activeSeconds } }),
        credentials: "same-origin",
        keepalive: true
      })
      if (response.status === 422) {
        // Retrying the same unprocessable request cannot recover without a reload.
        this.telemetryRejected = true
        return
      }
      const legal = await legalAcceptanceRequired(response)
      if (legal) {
        this.telemetryRejected = true
        presentLegalAcceptance(legal)
        return
      }
      if (!response.ok) throw new Error(`Request failed with ${response.status}`)
    } catch (error) {
      if (this.sessionId === sessionId) this.lastSentSeconds = -1
      console.debug("Unable to save reading time", error)
    }
  }

  headers() {
    const headers = { Accept: "application/json", "Content-Type": "application/json" }
    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (token) headers["X-CSRF-Token"] = token
    return headers
  }

  accessModeChanged(event) {
    if (event.detail.offline) {
      this.disable()
    } else {
      this.enable()
    }
  }

  disable() {
    if (!this.enabled) return

    this.enabled = false
    window.clearInterval(this.tickTimer)
    window.clearInterval(this.flushTimer)
  }
}
